/**
 * Strava Lap-to-HYROX Inference Engine
 * Pure parsing function evaluating Strava laps, split distances, and names to classify HYROX workout intervals.
 *
 * @module integrations/laps
 */

export const OFFICIAL_HYROX_STATIONS = [
  'SkiErg',
  'Sled Push',
  'Sled Pull',
  'Burpee Broad Jumps',
  'Rowing',
  'Farmers Carry',
  'Sandbag Lunges',
  'Wall Balls'
];

/**
 * Attempts to match a raw lap name or text against official HYROX stations or transitions.
 * @param {string} [name] - Raw lap name or description
 * @returns {{ stationName: string, type: string, stageType: string } | null}
 */
export function matchHyroxStation(name) {
  if (!name || typeof name !== 'string') return null;
  const lower = name.toLowerCase().trim();

  if (lower.includes('roxzone') || lower.includes('rox zone') || lower.includes('transition')) {
    return { stationName: 'RoxZone', type: 'RoxZone', stageType: 'RoxZone' };
  }
  if (lower.includes('ski')) {
    return { stationName: 'SkiErg', type: 'Strength', stageType: 'Station' };
  }
  if (lower.includes('sled push') || lower.includes('push sled')) {
    return { stationName: 'Sled Push', type: 'Strength', stageType: 'Station' };
  }
  if (lower.includes('sled pull') || lower.includes('pull sled')) {
    return { stationName: 'Sled Pull', type: 'Strength', stageType: 'Station' };
  }
  if (lower.includes('burpee')) {
    return { stationName: 'Burpee Broad Jumps', type: 'Strength', stageType: 'Station' };
  }
  if (lower.includes('row')) {
    return { stationName: 'Rowing', type: 'Strength', stageType: 'Station' };
  }
  if (lower.includes('farmer')) {
    return { stationName: 'Farmers Carry', type: 'Strength', stageType: 'Station' };
  }
  if (lower.includes('lunge') || lower.includes('sandbag')) {
    return { stationName: 'Sandbag Lunges', type: 'Strength', stageType: 'Station' };
  }
  if (lower.includes('wall ball') || lower.includes('wallball')) {
    return { stationName: 'Wall Balls', type: 'Strength', stageType: 'Station' };
  }
  if (lower.includes('run')) {
    return { stationName: name.trim(), type: 'Run', stageType: 'Run' };
  }

  return null;
}

/**
 * Pure function inferring HYROX stations, RoxZones, and running splits from raw Strava lap payloads.
 *
 * @param {Array<Object>} laps - Array of Strava lap objects.
 * @param {Object} [options={}] - Inference options.
 * @param {boolean} [options.strictAlternating=false] - Force 1-Run, 2-Station alternating pattern regardless of lap names.
 * @returns {Array<Object>} Normalized split stages.
 */
export function inferSplitsFromStravaLaps(laps, options = {}) {
  if (!Array.isArray(laps)) {
    return [];
  }

  const inferred = [];
  const stations = OFFICIAL_HYROX_STATIONS;

  laps.forEach((lap, index) => {
    if (!lap || typeof lap !== 'object') return;

    const splitNum = index + 1;
    const rawName = lap.name || '';
    const namedMatch = !options.strictAlternating ? matchHyroxStation(rawName) : null;

    let type;
    let stageType;
    let stationName;

    if (namedMatch) {
      type = namedMatch.type;
      stageType = namedMatch.stageType;
      stationName = namedMatch.stationName;
    } else {
      // Default HYROX Alternating Pattern:
      // Odd splits (1, 3, 5...) are Runs; Even splits (2, 4, 6...) are Stations.
      const isRun = splitNum % 2 !== 0;
      type = isRun ? 'Run' : 'Strength';
      stageType = isRun ? 'Run' : 'Station';

      if (isRun) {
        const runIdx = Math.ceil(splitNum / 2);
        stationName = `Run ${runIdx}`;
      } else {
        const stationIdx = Math.floor((splitNum - 1) / 2) % stations.length;
        stationName = stations[stationIdx];
      }
    }

    // Duration formatting
    const durationTotalSecs = Number.isFinite(lap.elapsed_time) ? Math.max(0, lap.elapsed_time) : (Number.isFinite(lap.moving_time) ? Math.max(0, lap.moving_time) : 0);
    const totalSecs = Math.round(durationTotalSecs);
    const durationMin = Math.floor(totalSecs / 60);
    const durationSec = totalSecs % 60;
    const timeString = `${String(durationMin).padStart(2, '0')}:${String(durationSec).padStart(2, '0')}`;

    // Distance conversion
    const distanceMeters = Number.isFinite(lap.distance) ? Math.max(0, lap.distance) : 0;
    let distanceDisplay = 'N/A';
    if (type === 'Run' || stageType === 'Run') {
      distanceDisplay = distanceMeters > 0 ? `${(distanceMeters / 1000).toFixed(2)} km` : '1.00 km';
    }

    const avgHr = Number.isFinite(lap.average_heartrate) ? Math.round(lap.average_heartrate) : 0;
    const maxHr = Number.isFinite(lap.max_heartrate) ? Math.round(lap.max_heartrate) : 0;

    inferred.push({
      lapIndex: splitNum,
      stageNum: splitNum,
      type: type,
      stageType: stageType,
      stationName: stationName,
      elapsedTime: timeString,
      durationMin: durationMin,
      durationSec: durationSec,
      durationSecs: durationTotalSecs,
      distance: distanceMeters,
      distanceDisplay: distanceDisplay,
      startDate: lap.start_date || null,
      startIndex: lap.start_index !== undefined ? lap.start_index : null,
      endIndex: lap.end_index !== undefined ? lap.end_index : null,
      avgHr: avgHr,
      maxHr: maxHr
    });
  });

  return inferred;
}
